/**
 * Opening-hours step (#1498).
 *
 * Not `WorkshopSettingsForm`: that form also edits VAT, lunch, and hour
 * exceptions. This step only confirms the weekly grid provisioning already
 * seeded (Mon–Fri 08–17). Persistence is `commitHoursStep`.
 */

import { Box, Divider, FormControlLabel, Switch, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { WorkshopOpeningHours } from '@/types';

const DAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const satisfies readonly (keyof WorkshopOpeningHours)[];

interface HoursStepProps {
  hours: WorkshopOpeningHours;
  onChange: (hours: WorkshopOpeningHours) => void;
}

export function HoursStep({ hours, onChange }: HoursStepProps) {
  const { t } = useTranslation('onboarding');
  const { t: ts } = useTranslation('settings');

  const setDay = (day: keyof WorkshopOpeningHours, value: WorkshopOpeningHours[keyof WorkshopOpeningHours]) => {
    onChange({ ...hours, [day]: value });
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('hours.intro')}
      </Typography>

      {DAY_KEYS.map((day, index) => {
        const dayHours = hours[day];
        const isOpen = dayHours !== null;
        const dayLabel = ts(`days.${day}`);

        return (
          <Box key={day}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                py: 1.5,
                flexWrap: 'wrap',
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={isOpen}
                    onChange={(e) =>
                      setDay(day, e.target.checked ? { open: '08:00', close: '17:00' } : null)
                    }
                  />
                }
                label={
                  <Typography variant="body2" sx={{ width: 100 }}>
                    {dayLabel}
                  </Typography>
                }
                sx={{ m: 0, minWidth: 160 }}
              />

              {isOpen && dayHours && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    size="small"
                    type="time"
                    value={dayHours.open}
                    onChange={(e) => setDay(day, { ...dayHours, open: e.target.value })}
                    sx={{ width: 120 }}
                    slotProps={{
                      inputLabel: { shrink: true },
                      htmlInput: { 'aria-label': t('hours.openTime', { day: dayLabel }) },
                    }}
                  />
                  <Typography color="text.secondary">-</Typography>
                  <TextField
                    size="small"
                    type="time"
                    value={dayHours.close}
                    onChange={(e) => setDay(day, { ...dayHours, close: e.target.value })}
                    sx={{ width: 120 }}
                    slotProps={{
                      htmlInput: { 'aria-label': t('hours.closeTime', { day: dayLabel }) },
                    }}
                  />
                </Box>
              )}

              {!isOpen && (
                <Typography variant="body2" color="text.secondary">
                  {ts('closed')}
                </Typography>
              )}
            </Box>
            {index < DAY_KEYS.length - 1 && <Divider />}
          </Box>
        );
      })}
    </Box>
  );
}
